/**
 * UAVTradeZone Modification Record
 * 
 * Change ID: PHASE_2_SCHEDULED_JOBS_ADMIN_UI
 * Date: 2025-01-03
 * Modified By: Cursor
 * 
 * Description:
 * Admin page for viewing and managing scheduled jobs
 * Allows filtering, viewing status, and cancelling pending jobs
 * 
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Target: 200 lines max (Component limit)
 * - Strategy: Split into sub-components if needed
 */

import React, { useState, useEffect } from 'react';
// [2025-01-04] Fix: Removed unused Dropdown import
import { Table, Alert, Spinner, Card, Form } from 'react-bootstrap';
import schedulerApi from '../../../services/schedulerApi';
import './AdminScheduledJobsPage.scss'; // [2025-01-03] Admin UI: Use SCSS module
import AdminButton from '../../../components/admin/AdminButton';
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_SCHEDULED_JOBS
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Replaces react-bootstrap Button usages with AdminButton so scheduled jobs
 * actions follow the design system tokens.
 *
 * File Size Check:
 * - Current: 150
 * - Adding: ~12
 * - Projected: ~162
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-danger-figma / btn-figma-1)
 * - Tokens used: --font-size-body-small, --line-height-body-small
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/scheduler/AdminScheduledJobsPage.tsx
 */

const AdminScheduledJobsPage: React.FC = () => {
  // [2025-01-03] Admin UI: State management
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancellingJobId, setCancellingJobId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('pending');
  const [filterJobType, setFilterJobType] = useState<string>('');

  // [2025-01-04] Fix: Added loadScheduledJobs to dependency array with useCallback
  const loadScheduledJobs = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await schedulerApi.getScheduledJobs({ status: filterStatus, job_type: filterJobType, limit: 50 });
      setJobs(response.data.jobs);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to load scheduled jobs' });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterJobType]);

  useEffect(() => {
    loadScheduledJobs();
  }, [loadScheduledJobs]);

  const handleCancelJob = async (jobId: string) => {
    // [2025-01-03] Admin UI: Cancel a pending job
    if (!window.confirm('Are you sure you want to cancel this job?')) return;

    try {
      setCancellingJobId(jobId);
      setMessage(null);

      await schedulerApi.cancelJob(jobId);

      setMessage({ type: 'success', text: 'Job cancelled successfully.' });
      loadScheduledJobs(); // Reload jobs after cancellation

    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to cancel job' });
    } finally {
      setCancellingJobId(null);
    }
  };

  const formatDate = (dateString: string) => {
    // [2025-01-03] Admin UI: Format timestamp
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="admin-scheduled-jobs-page">
      <h2>Scheduled Jobs</h2>

      {message && (
        <Alert variant={message.type === 'success' ? 'success' : 'danger'} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Filters</Card.Title>
          <Form className="d-flex gap-3">
            <Form.Group controlId="filterStatus">
              <Form.Label>Status</Form.Label>
              <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="filterJobType">
              <Form.Label>Job Type</Form.Label>
              <Form.Select value={filterJobType} onChange={(e) => setFilterJobType(e.target.value)}>
                <option value="">All</option>
                <option value="backup">Backup</option>
                <option value="restore">Restore</option>
                <option value="exchange_rates">Exchange Rates</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Card.Title>Job List</Card.Title>
          
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : jobs.length === 0 ? (
            <Alert variant="info">No scheduled jobs found matching criteria.</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Scheduled For</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Environment</th>
                  <th>Requested By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job._id}>
                    <td>{formatDate(job.scheduled_for)}</td>
                    <td className="text-capitalize">{job.job_type.replace('_', ' ')}</td>
                    <td className="text-capitalize">{job.status}</td>
                    <td className="text-uppercase">{job.environment}</td>
                    <td>{job.requested_by?.email || 'N/A'}</td>
                    <td>
                    {job.status === 'pending' && (
                      <AdminButton
                        intent="danger"
                        size="sm"
                        onClick={() => handleCancelJob(job._id)}
                        disabled={cancellingJobId === job._id}
                      >
                        {cancellingJobId === job._id ? 'Cancelling...' : 'Cancel'}
                      </AdminButton>
                    )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminScheduledJobsPage;